import React, { useEffect, useMemo } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  CardMedia,
} from "@mui/material";
import { useGetCommunityGroup } from "../../api/hooks/useCommunity";
import { useGetCategories } from "../../api/hooks/useCategory";
import { showError } from "../../helpers/toast";
import type { CommunityGroup } from "../../api/types/community.types";
import type { Category } from "../../api/types/category.types";

// Types
interface ReportedMessage {
  message: string;
  reportedBy: string;
  reason: string;
  date: string;
  status: string;
}

const ViewCommunityGroup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get group data from navigation state
  const groupData = (location.state && location.state.group) as CommunityGroup | undefined;
  const groupId = groupData?._id;

  // Fetch group data from API if we have an ID
  const { data: groupDetailData, isLoading: isLoadingGroup, error: groupError } = useGetCommunityGroup(
    groupId || '',
    { enabled: !!groupId }
  );

  // Fetch categories for category name lookup
  const { data: categoriesData } = useGetCategories({}, { enabled: true });

  // Extract categories for lookup
  const categories = useMemo(() => {
    const data = categoriesData?.data;
    let cats: Category[] = [];
    
    if (Array.isArray(data)) {
      cats = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as any;
      if ('list' in dataObj && Array.isArray(dataObj.list)) {
        cats = dataObj.list;
      } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
        cats = dataObj.data;
      }
    }
    
    return cats.filter((cat) => cat.status && !cat.isDeleted);
  }, [categoriesData]);

  // Create category map for quick lookup
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((cat) => {
      map.set(cat._id, cat.name);
    });
    return map;
  }, [categories]);

  // Use API data if available, otherwise use state data
  const group = groupDetailData?.data || groupData;

  // Helper function to get category name (handles both string and object categoryId)
  const getCategoryName = (categoryId: any): string => {
    if (!categoryId) return "-";
    if (typeof categoryId === "string") {
      return categoryMap.get(categoryId) || "-";
    }
    if (typeof categoryId === "object" && categoryId.name) {
      return categoryId.name;
    }
    if (typeof categoryId === "object" && categoryId._id) {
      return categoryMap.get(categoryId._id) || categoryId.name || "-";
    }
    return "-";
  };

  // Format date helper
  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Handle error
  useEffect(() => {
    if (groupError) {
      showError((groupError as any)?.response?.data?.message || "Failed to load group details");
    }
  }, [groupError]);

  // Redirect if no group data
  useEffect(() => {
    if (!group && !isLoadingGroup) {
      showError("No group selected. Redirecting to community groups list.");
      navigate("/Community");
    }
  }, [group, isLoadingGroup, navigate]);

  // Default reported messages (empty array if not provided)
  const reportedMessages: ReportedMessage[] = (group as any)?.reportedMessages || [];

  if (isLoadingGroup) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!group) {
    return null;
  }

  return (
    <>
      <div className="main_title">
        <h1>Community Group Details</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-community")}>
            Manage Community Groups
          </button>{" "}
          - View Group
        </p>
      </div>

      <div className="cards">
        <div className="view_page">
          <div className="gap_p">

            {/* Group Info */}
            <div className="w_33">
              <h6>Group Name</h6>
              <p>{group.name || "-"}</p>
            </div>

            {group.image && (
              <div className="w_33">
                <h6>Group Image</h6>
                <Box sx={{ maxWidth: 200, borderRadius: 1, overflow: "hidden" }}>
                  <CardMedia component="img" image={group.image} alt={group.name || "Group"} sx={{ objectFit: "contain", maxHeight: 160 }} />
                </Box>
              </div>
            )}

            <div className="w_33">
              <h6>Category</h6>
              <p>{getCategoryName(group.categoryId)}</p>
            </div>

            <div className="w_33">
              <h6>Members Count</h6>
              <p>{group.memberCount || 0}</p>
            </div>

            <div className="w_100">
              <h6>Description</h6>
              <div
                className="rich-content"
                dangerouslySetInnerHTML={{
                  __html: group.description && group.description.trim() ? group.description : "<p>-</p>",
                }}
              />
            </div>

            <div className="w_33">
              <h6>Created Date</h6>
              <p>{formatDate(group.createdAt)}</p>
            </div>

            <div className="w_33">
              <h6>Status</h6>
              <p>
                <SwitchToggle defaultChecked={group.status ?? true} />
              </p>
            </div>

            {/* Reported Messages Table */}
            <div className="w_100 mt_30">
              <h4>Reported / Flagged Messages</h4>

              <TableContainer className="table_container">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Message Preview</TableCell>
                      <TableCell>Reported By</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Report Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {reportedMessages && reportedMessages.length > 0 ? (
                      reportedMessages.map((msg, index) => (
                        <TableRow key={index}>
                          <TableCell>{msg.message}</TableCell>
                          <TableCell>{msg.reportedBy}</TableCell>
                          <TableCell>{msg.reason}</TableCell>
                          <TableCell>{msg.date}</TableCell>
                          <TableCell>{msg.status}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} style={{ textAlign: "center" }}>
                          No reports found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>

                </Table>
              </TableContainer>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ViewCommunityGroup;
